import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  User, 
  Trophy, 
  Gamepad2, 
  Percent,
  Calendar,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Globe,
  Loader2,
  CheckCircle2,
  Clock,
  AlertCircle,
  Bot,
  Zap,
  Brain,
  Crown,
  TrendingUp,
  Flame,
  Settings,
  Download
} from "lucide-react";
import KYCVerification from "@/components/KYCVerification";
import RatingStats from "@/components/RatingStats";
import LeagueCard from "@/components/LeagueCard";
import { API_URL } from "@/config/api";

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, fetchUser, setLanguage, language, fetchGameHistory, token } = useStore();
  
  const [loading, setLoading] = useState(false);
  const [gameHistory, setGameHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [botStats, setBotStats] = useState(null);
  const [botStatsLoading, setBotStatsLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setHistoryLoading(true);
    setBotStatsLoading(true);
    
    const history = await fetchGameHistory();
    setGameHistory(history);
    setHistoryLoading(false);

    // Fetch bot stats
    try {
      const res = await fetch(`${API_URL}/api/bot/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBotStats(data);
      }
    } catch (err) {
      console.error("Failed to load bot stats:", err);
    }
    setBotStatsLoading(false);
  };

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    i18n.changeLanguage(lang);
    toast.success("Language updated");
  };

  // Export PGN for PvP game
  const handleExportPGN = async (gameId) => {
    try {
      const res = await fetch(`${API_URL}/api/games/${gameId}/export-pgn`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `teachess_pvp_${gameId.slice(0, 8)}.pgn`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success("PGN экспортирован");
      } else {
        toast.error("Ошибка экспорта");
      }
    } catch (err) {
      toast.error("Ошибка экспорта");
    }
  };

  const getKycBadge = () => {
    switch (user?.kyc_status) {
      case "approved":
        return (
          <Badge className="kyc-approved">
            <ShieldCheck className="h-3 w-3 mr-1" />
            {t("profile.kyc.approved")}
          </Badge>
        );
      case "pending":
        return (
          <Badge className="kyc-pending">
            <Clock className="h-3 w-3 mr-1" />
            {t("profile.kyc.pending")}
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-500/20 text-red-500 border-red-500/30">
            <AlertCircle className="h-3 w-3 mr-1" />
            {t("kyc.rejected")}
          </Badge>
        );
      default:
        return (
          <Badge className="kyc-not-started">
            <ShieldAlert className="h-3 w-3 mr-1" />
            {t("profile.kyc.notStarted")}
          </Badge>
        );
    }
  };

  const winRate = user?.games_played > 0 
    ? ((user.games_won / user.games_played) * 100).toFixed(1) 
    : "0.0";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <div className="space-y-8" data-testid="profile-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-full bg-primary/20 flex items-center justify-center">
            <User className="h-10 w-10 text-primary" />
          </div>
          <div>
            <h1 className="text-3xl font-black tracking-tight">{user?.username}</h1>
            <p className="text-muted-foreground">{user?.email}</p>
            <div className="mt-2">{getKycBadge()}</div>
          </div>
        </div>
        <Button 
          onClick={() => navigate("/achievements")}
          className="btn-gold"
          data-testid="achievements-btn"
        >
          <Trophy className="h-4 w-4 mr-2" />
          {t("achievements.title")}
        </Button>
        <Button 
          onClick={() => navigate("/settings")}
          variant="outline"
          data-testid="settings-btn"
        >
          <Gamepad2 className="h-4 w-4 mr-2" />
          {t("settings.title")}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Stats */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Trophy className="h-5 w-5 text-primary" />
              Statistics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("profile.rating")}</span>
              <span className="font-accent text-2xl font-bold text-primary">{user?.rating || 1500}</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("profile.gamesPlayed")}</span>
              <span className="font-bold">{user?.games_played || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("profile.gamesWon")}</span>
              <span className="font-bold text-green-500">{user?.games_won || 0}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("profile.winRate")}</span>
              <span className="font-bold">{winRate}%</span>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">{t("profile.memberSince")}</span>
              <span className="text-sm">{formatDate(user?.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Rating Stats */}
        <RatingStats token={token} />

        {/* League Card */}
        <LeagueCard token={token} />

        {/* KYC Verification - New Component */}
        <KYCVerification onComplete={() => fetchUser()} />

        {/* Settings */}
        <Card className="glass border-border/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Globe className="h-5 w-5 text-primary" />
              {t("profile.settings")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm text-muted-foreground">{t("profile.language")}</label>
              <Select value={language} onValueChange={handleLanguageChange}>
                <SelectTrigger data-testid="language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="ru">Русский</SelectItem>
                  <SelectItem value="zh">中文</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bot Statistics - Lichess Style */}
      <Card className="glass border-border/50" data-testid="bot-stats-section">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Bot className="h-5 w-5 text-primary" />
            {t("profile.botStats.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {botStatsLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : !botStats?.totals || botStats.totals.games === 0 ? (
            <div className="text-center py-10">
              <Bot className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">{t("profile.botStats.noGames")}</p>
              <Button onClick={() => navigate("/play-bot")} className="btn-gold">
                <Gamepad2 className="h-4 w-4 mr-2" />
                {t("profile.botStats.playNow")}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overall Stats */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="text-center p-4 rounded-lg bg-muted/30">
                  <p className="text-3xl font-bold text-primary">{botStats.totals.games}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.botStats.totalGames")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-green-500/10">
                  <p className="text-3xl font-bold text-green-500">{botStats.totals.wins}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.botStats.wins")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-red-500/10">
                  <p className="text-3xl font-bold text-red-500">{botStats.totals.losses}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.botStats.losses")}</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-yellow-500/10">
                  <p className="text-3xl font-bold text-yellow-500">{botStats.totals.draws}</p>
                  <p className="text-sm text-muted-foreground">{t("profile.botStats.draws")}</p>
                </div>
              </div>

              {/* Win Rate & Streaks */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30">
                  <TrendingUp className="h-8 w-8 text-primary" />
                  <div>
                    <p className="text-2xl font-bold">{botStats.totals.win_rate}%</p>
                    <p className="text-sm text-muted-foreground">{t("profile.botStats.winRate")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30">
                  <Flame className="h-8 w-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold">{botStats.streaks.current}</p>
                    <p className="text-sm text-muted-foreground">{t("profile.botStats.currentStreak")}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30">
                  <Crown className="h-8 w-8 text-yellow-500" />
                  <div>
                    <p className="text-2xl font-bold">{botStats.streaks.best}</p>
                    <p className="text-sm text-muted-foreground">{t("profile.botStats.bestStreak")}</p>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Stats by Difficulty */}
              <div>
                <h3 className="font-semibold mb-4">{t("profile.botStats.byDifficulty")}</h3>
                <div className="space-y-4">
                  {[
                    { key: "easy", icon: Zap, color: "text-green-500", bg: "bg-green-500" },
                    { key: "medium", icon: Bot, color: "text-yellow-500", bg: "bg-yellow-500" },
                    { key: "hard", icon: Brain, color: "text-orange-500", bg: "bg-orange-500" },
                    { key: "master", icon: Crown, color: "text-red-500", bg: "bg-red-500" }
                  ].map(({ key, icon: Icon, color, bg }) => {
                    const stats = botStats.by_difficulty?.[key] || { wins: 0, losses: 0, draws: 0, total: 0 };
                    const winRate = stats.total > 0 ? Math.round((stats.wins / stats.total) * 100) : 0;
                    
                    return (
                      <div key={key} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Icon className={`h-4 w-4 ${color}`} />
                            <span className="font-medium capitalize">
                              {t(`game.difficulty.${key}`)}
                            </span>
                          </div>
                          <div className="flex items-center gap-4 text-sm">
                            <span className="text-green-500">{stats.wins}W</span>
                            <span className="text-red-500">{stats.losses}L</span>
                            <span className="text-yellow-500">{stats.draws}D</span>
                            <span className="text-muted-foreground">({stats.total})</span>
                          </div>
                        </div>
                        <div className="relative">
                          <Progress value={winRate} className="h-2" />
                          <span className="absolute right-0 -top-6 text-xs text-muted-foreground">
                            {winRate}%
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Recent Bot Games */}
              {botStats.recent_games && botStats.recent_games.length > 0 && (
                <>
                  <Separator />
                  <div>
                    <h3 className="font-semibold mb-4">{t("profile.botStats.recentGames")}</h3>
                    <div className="space-y-2">
                      {botStats.recent_games.slice(0, 5).map((game) => {
                        const didWin = game.winner === game.player_color;
                        const isDraw = game.winner === "draw";
                        
                        return (
                          <div 
                            key={game.id}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors cursor-pointer"
                            onClick={() => navigate(`/bot-game/${game.id}`)}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`h-8 w-8 rounded-full flex items-center justify-center ${
                                didWin ? "bg-green-500/20" : isDraw ? "bg-yellow-500/20" : "bg-red-500/20"
                              }`}>
                                {didWin ? (
                                  <Trophy className="h-4 w-4 text-green-500" />
                                ) : isDraw ? (
                                  <Percent className="h-4 w-4 text-yellow-500" />
                                ) : (
                                  <User className="h-4 w-4 text-red-500" />
                                )}
                              </div>
                              <div>
                                <p className="font-medium text-sm">
                                  vs Bot ({String(game.difficulty).startsWith("level_") ? `${t("game.level")} ${String(game.difficulty).replace("level_", "")}` : t(`game.difficulty.${game.difficulty}`)})
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {game.time_control ? `${Math.floor(game.time_control / 60)}m • ` : ""}{game.player_color === "white" ? "♔" : "♚"}
                                </p>
                              </div>
                            </div>
                            <div className="text-right">
                              <p className={`font-bold text-sm ${
                                didWin ? "text-green-500" : isDraw ? "text-yellow-500" : "text-red-500"
                              }`}>
                                {didWin ? t("game.youWin").replace("!", "") : isDraw ? t("game.drawResult") : t("game.youLose")}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(game.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <Button 
                      variant="outline" 
                      className="w-full mt-4"
                      onClick={() => navigate("/play-bot")}
                    >
                      <Bot className="h-4 w-4 mr-2" />
                      {t("game.playWithBot")}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Game History */}
      <Card className="glass border-border/50">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Gamepad2 className="h-5 w-5 text-primary" />
            {t("profile.gameHistory", "Game History")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {historyLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : gameHistory.length === 0 ? (
            <div className="text-center py-10">
              <Gamepad2 className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No games played yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {gameHistory.slice(0, 10).map((game) => {
                const isWhite = game.white_player_id === user?.id;
                const opponent = isWhite ? game.black_username : game.white_username;
                const didWin = (game.winner === "white" && isWhite) || (game.winner === "black" && !isWhite);
                const isDraw = game.winner === "draw";

                return (
                  <div 
                    key={game.id}
                    className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                        didWin ? "bg-green-500/20" : isDraw ? "bg-yellow-500/20" : "bg-red-500/20"
                      }`}>
                        {didWin ? (
                          <Trophy className="h-5 w-5 text-green-500" />
                        ) : isDraw ? (
                          <Percent className="h-5 w-5 text-yellow-500" />
                        ) : (
                          <User className="h-5 w-5 text-red-500" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">vs {opponent || "Unknown"}</p>
                        <p className="text-xs text-muted-foreground">
                          {Math.floor(game.time_control / 60)}m + {game.increment}s
                          {game.stake > 0 && ` • ${game.stake} Points`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleExportPGN(game.id)}
                        className="h-8 px-2"
                        data-testid={`export-pgn-${game.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                      <div className="text-right">
                        <p className={`font-bold ${
                          didWin ? "text-green-500" : isDraw ? "text-yellow-500" : "text-red-500"
                        }`}>
                          {didWin ? t("profile.won", "Won") : isDraw ? t("profile.draw", "Draw") : t("profile.lost", "Lost")}
                        </p>
                        <p className="text-xs text-muted-foreground">{formatDate(game.created_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
